import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Navbar, Nav, Button, NavDropdown } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import logo from './logo.svg';
import './App.css';
import { authService } from './services/authService';
import { currentUserService } from './services/currentUserService';
import LoginForm from './components/LoginForm';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import RegisterForm from './components/RegisterForm';
import UserProfile from './components/UserProfile';
import ProtectedRoute from './components/ProtectedRoute';
import ProfilePage from './components/ProfilePage';
import { sectionAccessConfig } from './config/roleAccess';
import { RoleName } from './api-client/models/RoleName';
import UsersModule from './users/UsersModule';
import FeedbackHrOneOnOneModule from './feedback-hr-one-on-one/FeedbackHrOneOnOneModule';
import FeedbackLawyerConsultationModule from './feedback-lawyer-consultation/FeedbackLawyerConsultationModule';
import MyFeedbackHrOneOnOneModule from './feedback-hr-one-on-one/MyFeedbackHrOneOnOneModule';
import MyFeedbackLawyerConsultationModule from './feedback-lawyer-consultation/MyFeedbackLawyerConsultationModule';
import HiringModule from './hiring/HiringModule';
import MyHiringReferralsModule from './hiring/MyHiringReferralsModule';
import VacationModule from './vacation/VacationModule';
import MyVacationModule from './vacation/MyVacationModule';
import MyDayOffModule from './vacation/MyDayOffModule';
import SickLeaveModule from './sick-leave/SickLeaveModule';
import MySickLeaveModule from './sick-leave/MySickLeaveModule';

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [userRoles, setUserRoles] = useState<RoleName[]>([]);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    authService.removeToken();
    currentUserService.clearUser();
    setIsLoggedIn(false);
    setUserRoles([]);
  };

  // Проверяем аутентификацию при монтировании компонента и подписываемся на изменения
  useEffect(() => {
    // Начальная проверка
    setIsLoggedIn(authService.isAuthenticated());

    // Загружаем роли пользователя
    const loadUserRoles = async () => {
      const roles = await currentUserService.getUserRoles();
      setUserRoles(roles);
    };

    if (isLoggedIn) {
      loadUserRoles();
    }

    // Подписка на изменения состояния аутентификации (в том числе из других вкладок)
    const unsubscribe = authService.onAuthChange(() => {
      const authenticated = authService.isAuthenticated();
      setIsLoggedIn(authenticated);
      if (authenticated) {
        loadUserRoles();
      } else {
        setUserRoles([]);
      }
    });

    return unsubscribe;
  }, [isLoggedIn]);

  // Проверка доступа к разделу
  const hasAccess = (requiredRoles: RoleName[]): boolean => {
    return requiredRoles.some((role) => userRoles.includes(role));
  };

  // Рендер навигации с учетом прав доступа
  const renderNavigation = () => {
    const navItems: React.ReactNode[] = [];

    sectionAccessConfig.forEach((section) => {
      if (!hasAccess(section.requiredRoles)) {
        return;
      }

      // Пропускаем профиль в главном меню, он будет отображаться отдельно
      if (section.path === '/profile') {
        return;
      }

      if (section.isDropdown && section.children) {
        const visibleChildren = section.children.filter((child) => {
          if (!child.requiredRoles || child.requiredRoles.length === 0) {
            return true;
          }
          return child.requiredRoles.some((role) => userRoles.includes(role));
        });

        if (visibleChildren.length === 0) {
          return;
        }

        navItems.push(
          <NavDropdown
            key={section.path}
            title={section.label}
            id={`nav-dropdown-${section.path.replace(/\//g, '-')}`}
          >
            {visibleChildren.map((child) => (
              <NavDropdown.Item key={child.path} href={child.path}>
                {child.label}
              </NavDropdown.Item>
            ))}
          </NavDropdown>
        );
      } else {
        navItems.push(
          <Nav.Link key={section.path} href={section.path}>
            {section.label}
          </Nav.Link>
        );
      }
    });

    return navItems;
  };

  if (isLoggedIn) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <Navbar bg="dark" variant="dark" expand="lg" className="px-3">
          <Navbar.Brand href="/users">
            <img
              src={logo}
              width="30"
              height="30"
              className="d-inline-block align-top me-2"
              alt="HR Service Logo"
            />
            Админ панель HR-сервиса
          </Navbar.Brand>
          <Nav className="ms-auto">
            {renderNavigation()}
            <UserProfile />
            <Button variant="outline-light" onClick={handleLogout}>
              Выйти
            </Button>
          </Nav>
        </Navbar>

        <Container fluid className="flex-grow-1 py-4">
          <Routes>
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.ADMIN]}>
                  <UsersModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback/hr-one-on-one/my/*"
              element={
                <ProtectedRoute>
                  <MyFeedbackHrOneOnOneModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback/hr-one-on-one/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.HR, RoleName.ADMIN]}>
                  <FeedbackHrOneOnOneModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback/lawyer-consultation/my/*"
              element={
                <ProtectedRoute>
                  <MyFeedbackLawyerConsultationModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback/lawyer-consultation/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.LAWYER, RoleName.ADMIN]}>
                  <FeedbackLawyerConsultationModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hiring/my/*"
              element={
                <ProtectedRoute>
                  <MyHiringReferralsModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hiring/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.RECRUITER, RoleName.ADMIN]}>
                  <HiringModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vacation/my/*"
              element={
                <ProtectedRoute>
                  <MyVacationModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vacation/day-off/my/*"
              element={
                <ProtectedRoute>
                  <MyDayOffModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vacation/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.ACCOUNTANT, RoleName.ADMIN]}>
                  <VacationModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sick-leave/my/*"
              element={
                <ProtectedRoute>
                  <MySickLeaveModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sick-leave/*"
              element={
                <ProtectedRoute requiredRoles={[RoleName.ACCOUNTANT, RoleName.ADMIN]}>
                  <SickLeaveModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Navigate to="/profile" replace />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Container>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Container
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: '100vh' }}
          >
            <Row className="w-100">
              <Col xs={12} sm={10} md={8} lg={6} xl={4} className="mx-auto">
                <LoginForm onSuccess={handleLoginSuccess} />
              </Col>
            </Row>
          </Container>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Container
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: '100vh' }}
          >
            <Row className="w-100">
              <Col xs={12} sm={10} md={8} lg={6} xl={4} className="mx-auto">
                <ForgotPasswordPage />
              </Col>
            </Row>
          </Container>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Container
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: '100vh' }}
          >
            <Row className="w-100">
              <Col xs={12} sm={10} md={8} lg={6} xl={4} className="mx-auto">
                <ResetPasswordPage />
              </Col>
            </Row>
          </Container>
        }
      />
      <Route
        path="/register"
        element={
          <Container
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: '100vh' }}
          >
            <Row className="w-100">
              <Col xs={12} sm={10} md={8} lg={6} xl={4} className="mx-auto">
                <RegisterForm />
              </Col>
            </Row>
          </Container>
        }
      />
      <Route path="*" element={<LoginWithRedirect onLoginSuccess={handleLoginSuccess} />} />
    </Routes>
  );
}

// Компонент для редиректа на логин с сохранением текущего URL
interface LoginWithRedirectProps {
  onLoginSuccess: () => void;
}

function LoginWithRedirect({ onLoginSuccess }: LoginWithRedirectProps) {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath !== '/login') {
      authService.setReturnUrl(currentPath);
    }
  }, [location]);

  return (
    <Navigate
      to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
      replace
    />
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
